import * as React from 'react';
import {
  Modal,
  Form,
  Input,
  Row,
  Col,
  Button,
  Select,
  TimePicker,
  Checkbox,
  DatePicker,
  InputNumber,
  Radio,
  message,
} from 'antd';
import { HocOptions } from 'src/common/generic-hoc.types';
import GenericHoc from 'src/common/generic-hoc';
import { useTranslation } from 'react-i18next';
import { useDebounce } from 'src/library/hooks/useDebounce';
import {
  getAddressHierarchyData,
} from '../AssignedDrivers/AssignedDrivers.api';
import { vehicleFieldList, weekDays, AddNewVehicleModalProps } from './AddNewVehicleModal.utils';
import { fetchVehicleInfo, updateVehicle, addVehicle } from './AddNewVehicleModal.api';
import moment from 'moment';
import {
  useVendorFetcher,
  useHubFetcher,
  isFieldDisabled,
  renderHubCodeSelect,
  generateDefaultLabels,
  generateFieldListMap,
} from '../../../common/AddDriverVehicleUtils';
import {
  fetchHubsDetails as fetchVehicleMakeDetails,
} from 'src/components/pages/VehicleRequests/VehicleRequests.utils';

const { Option } = Select;

const AddNewVehicleModal: React.FC<AddNewVehicleModalProps> = (props: AddNewVehicleModalProps) => {
  const [form] = Form.useForm();
  const {
    handleModalClose,
    isVisible,
    action,
    vehicleData,
    reloadView,
    fields_list,
    vehicle_validation_strategy,
    primaryTrackingSources,
  } = props;
  const { t } = useTranslation();

  // Local state for form data
  const [vehicleMakes, setVehicleMakes] = React.useState<any[]>([]);
  const [confirmLoading, setConfirmLoading] = React.useState<boolean>(false);
  const [hubQuery, setHubQuery] = React.useState('');
  const [stateOptions, setStateOptions] = React.useState<any[]>([]);
  const [stateLoading, setStateLoading] = React.useState<boolean>(false);
  const [selectedVendor, setSelectedVendor] = React.useState<
    { label: string; value: string } | undefined
  >();

  // Debounce hub search query to avoid excessive API calls
  const debouncedHubQuery = useDebounce(hubQuery, 500);

  // Custom hooks for vendor and hub data fetching
  const { vendorNames, vendorLoading, fetchVendorList } = useVendorFetcher(form, t, 'vehicle_vendor_code');
  const { hubOptions } = useHubFetcher(debouncedHubQuery, t);

  // Generate label mappings and field configuration
  const defaultLabels = generateDefaultLabels(vehicleFieldList);
  const fieldConfigMap = generateFieldListMap(fields_list);
  const customFieldsList = (fields_list || []).filter((field: any) => field.isCustom);

  const setVendorCode = (vehicleVendorCode: string) => {
    if (!vehicleVendorCode || !vendorNames?.length) return;
    const vendor = vendorNames.find((v) => v.code === vehicleVendorCode);
    if (vendor) {
      form.setFieldsValue({ vehicle_vendor_code: vendor.code });
      setSelectedVendor({
        label: vendor.code,
        value: vendor.id,
      });
    }
  };

  const handleVendorChange = (value: string) => {
    const vendor = vendorNames.find((v) => v.id === value);
    if (vendor) {
      const vendorObj = {
        label: vendor.code,
        value: vendor.id,
      };
      setSelectedVendor(vendorObj);
      form.setFieldsValue({ vehicle_vendor_code: vendor.code });
    }
  };

  // Format vehicle data from API response to form-friendly format
  const formatVehicleData = (data: any) => {
    if (!data) return {};

    const formatted: any = {
      ...data,
      hub_code: data.hub_code
        ? { value: data.hub_code, label: data.hub_name }
        : undefined,
      time_slot_start: data.time_slot_start ? moment(data.time_slot_start, 'HH:mm') : null,
      time_slot_end: data.time_slot_end ? moment(data.time_slot_end, 'HH:mm') : null,
      working_days: data?.working_days || [],
      is_available_for_midmile: data?.is_available_for_midmile || false,
      is_available_for_endpoint: data?.is_available_for_endpoint || false,
    };

    customFieldsList.forEach((field: any) => {
      const fieldValue = data[field.key];
      switch (field.type) {
        case 'datetime':
          if (fieldValue) {
            formatted[field.key] = moment(Number(fieldValue));
          }
          break;
        case 'dropdown':
          if (
            field.multiselect && fieldValue !== null
            && fieldValue !== undefined && !Array.isArray(fieldValue)
          ) {
            try {
              const parsed = JSON.parse(fieldValue);
              formatted[field.key] = Array.isArray(parsed) ? parsed : [fieldValue];
            } catch (e) {
              formatted[field.key] = typeof fieldValue === 'string' && fieldValue.includes(',')
                ? fieldValue.split(',').map((v: string) => v.trim())
                : [fieldValue];
            }
          }
          break;
        case 'jsonArray':
          if (fieldValue !== null && fieldValue !== undefined) {
            formatted[field.key] = typeof fieldValue === 'string'
              ? fieldValue
              : JSON.stringify(fieldValue, null, 2);
          }
          break;
        default:
          break;
      }
    });

    return formatted;
  };

  const fetchVehicleData = async () => {
    if (action === 'edit' && vehicleData?.id) {
      const response = await fetchVehicleInfo(vehicleData.id);
      if (!response?.isSuccess) {
        message.error(response?.errorMessage || t('Error fetching vehicle data'));
        return;
      }
      const pageData = response?.data?.page_data;
      if (!Array.isArray(pageData) || !pageData.length) {
        message.error(t('No vehicle data found for the given Vehicle'));
        return;
      }
      const formatted = formatVehicleData(pageData[0]);
      form.setFieldsValue(formatted);
      if (pageData[0]?.vehicle_vendor_code) {
        setVendorCode(pageData[0].vehicle_vendor_code);
      }
    }
  };

  const fetchVehicleMakes = async () => {
    try {
      const response = await fetchVehicleMakeDetails({ useCase: 'create_vehicle' }, 'vehicleMakeIdList');
      const options = (response || []).map((item: any) => ({
        value: item.code,
        label: `${item.code} - ${item.name}`,
      }));
      setVehicleMakes(options);
    } catch (err) {
      message.error(t('Error fetching vehicle makes'));
    }
  };

  /**
   * Fetch state options for state dropdown
   */
  const fetchStateOptions = async () => {
    setStateLoading(true);
    const payload = {
      location_key: 'state',
    };
    const response = await getAddressHierarchyData(payload);
    if (!response?.isSuccess) {
      message.error(response?.errorMessage || t('Error fetching states'));
      setStateLoading(false);
      return;
    }
    const options = response.data?.map((item: any) => ({
      value: item.code,
      label: `${item.code} - ${item.name}`,
    }));
    setStateOptions(options || []);
    setStateLoading(false);
  };

  // Fetch vehicle data when action or vehicleData.id changes
  React.useEffect(() => {
    fetchVehicleData();
  }, [action, vehicleData?.id]);

  React.useEffect(() => {
    fetchVendorList();
    fetchStateOptions();
    fetchVehicleMakes();
  }, []);

  const handleHubSearch = (value: string) => {
    setHubQuery(value);
  };

  /**
   * Handle form submission
   * Validates fields, formats payload, and calls create/update API
   */
  const handleSubmit = async () => {
    try {
      const values = await form.validateFields();
      let vendorCode = values.vehicle_vendor_code;
      if (selectedVendor?.label) {
        vendorCode = selectedVendor.label;
      }
      const payload = {
        ...values,
        hub_code: values.hub_code?.value,
        is_available_for_endpoint: values.is_available_for_endpoint || false,
        is_available_for_midmile: values.is_available_for_midmile || false,
        vehicle_make_code: values.vehicle_make_code,
        vehicle_vendor_code: vendorCode,
        state: values.state,
        time_slot_start: values.time_slot_start ? values.time_slot_start.format('HH:mm') : null,
        time_slot_end: values.time_slot_end ? values.time_slot_end.format('HH:mm') : null,
      };

      customFieldsList.forEach((field: any) => {
        const fieldValue = values[field.key];
        if (fieldValue === null || fieldValue === undefined || fieldValue === '') {
          payload[field.key] = null;
          return;
        }
        switch (field.type) {
          case 'datetime':
            payload[field.key] = moment(fieldValue).valueOf();
            break;
          case 'jsonArray':
            if (Array.isArray(fieldValue)) {
              payload[field.key] = fieldValue;
            } else {
              try {
                payload[field.key] = JSON.parse(fieldValue);
              } catch (e) {
                payload[field.key] = fieldValue;
              }
            }
            break;
          default:
            payload[field.key] = fieldValue;
            break;
        }
      });

      setConfirmLoading(true);

      // Call appropriate API based on action (edit or create)
      let response;
      if (action === 'edit' && vehicleData?.id) {
        response = await updateVehicle(vehicleData.id, payload);
      } else {
        response = await addVehicle(payload);
      }

      if (!response?.isSuccess) {
        message.error(response?.errorMessage || t('Error while saving vehicle'));
        setConfirmLoading(false);
        return;
      }

      message.success(t(`Vehicle ${action === 'edit' ? 'updated' : 'created'} successfully`));
      setConfirmLoading(false);
      handleModalClose();
      reloadView?.();
    } catch (err) {
      setConfirmLoading(false);
    }
  };

  /**
   * Build validation rules dynamically based on field configuration
   * Supports multiple validation strategies: regex, fixed_list, range, regex_list
   */
  const getValidationRules = (fieldKey: string, label: string, isOptional: boolean) => {
    const strategy = vehicle_validation_strategy?.[fieldKey];
    const rules: any[] = [];

    // Add required rule if field is not optional
    if (!isOptional) {
      rules.push({ required: true, message: `${label} is required!` });
    }

    if (strategy) {
      const errorMessage = strategy.error || `Invalid ${label}`;

      rules.push({
        validator: (_: any, value: any) => {
          // Allow empty values if strategy permits
          if (strategy.allow_empty && !value) {
            return Promise.resolve();
          }

          // Regex validation: single pattern
          if (value && strategy.strategy_type === 'regex') {
            const regex = new RegExp(strategy.regex);
            if (!regex.test(value)) {
              return Promise.reject(new Error(errorMessage));
            }
          }

          // Regex list validation: match any pattern in the list
          if (value && strategy.strategy_type === 'regex_list' && Array.isArray(strategy.list)) {
            const regexList = strategy.list.map((r: string) => new RegExp(r));
            if (!regexList.some((r) => r.test(value))) {
              return Promise.reject(new Error(errorMessage));
            }
          }

          // Fixed list validation: value must be in predefined list
          if (value && strategy.strategy_type === 'fixed_list') {
            if (!strategy.fixed_list.includes(value)) {
              return Promise.reject(new Error(errorMessage));
            }
          }

          // Range validation: numeric value within min-max range
          if (value && strategy.strategy_type === 'range') {
            const num = Number(value);
            if (Number.isNaN(num) || num < Number(strategy.min) || num > Number(strategy.max)) {
              return Promise.reject(new Error(errorMessage));
            }
          }

          return Promise.resolve();
        },
      });
    }

    return rules;
  };

  const renderInputBox = (placeholder: string, disabled: boolean) => (
    <Input placeholder={placeholder} disabled={disabled} />
  );

  const renderVendorSelect = (disabled) => {
    if (vendorLoading) {
      return renderInputBox(t('Loading vendors...'), disabled);
    }

    const vendorOptions = vendorNames?.map((vendor) => ({
      label: vendor.code,
      value: vendor.id,
    })) || [];

    return (
      <Select
        value={selectedVendor?.value}
        onChange={handleVendorChange}
        placeholder={t('Select Vendor Code')}
        showSearch
        showArrow
        disabled={disabled}
        style={{ width: '100%' }}
        options={vendorOptions}
      />
    );
  };

  /**
   * Generic select renderer for dropdowns
   * Supports multiple selection and search functionality
   */
  const renderSelect = (
    options: any[],
    label: string,
    disabled: boolean,
    multiple: boolean = false,
    loading?: boolean,
  ) => (
    <Select
      mode={multiple ? 'multiple' : undefined}
      placeholder={`Select ${label}`}
      allowClear
      showSearch
      optionFilterProp="children"
      loading={loading}
      disabled={disabled}
      style={{ width: '100%' }}
      filterOption
    >
      {options.map((opt: any) => (
        <Option key={opt.value} value={opt.value}>
          {opt.label}
        </Option>
      ))}
    </Select>
  );

  const renderFixedListSelect = (fieldKey: string, label: string, disabled: boolean) => {
    const options = (vehicle_validation_strategy?.[fieldKey]?.fixed_list || [])
      .map((val: string) => ({ label: val, value: val }));
    return renderSelect(options, label, disabled);
  };

  const renderCustomField = (fieldToShow: any, label: string, disabled: boolean) => {
    const options = (fieldToShow.options || []).map((opt: any) => ({
      label: opt.label || opt.text || opt.value,
      value: opt.value,
    }));
    switch (fieldToShow.type) {
      case 'text':
        return <Input placeholder={label} disabled={disabled} />;
      case 'number':
        return <InputNumber style={{ width: '100%' }} placeholder={label} disabled={disabled} />;
      case 'boolean':
        return (
          <Radio.Group disabled={disabled}>
            <Radio value>{t('Yes')}</Radio>
            <Radio value={false}>{t('No')}</Radio>
          </Radio.Group>
        );
      case 'dropdown':
        return renderSelect(options, label, disabled, !!fieldToShow.multiselect);
      case 'datetime':
        return <DatePicker showTime style={{ width: '100%' }} disabled={disabled} />;
      case 'jsonArray':
        return <Input.TextArea rows={3} placeholder={t('Enter JSON value')} disabled={disabled} />;
      case 'hyperlink':
        return <Input placeholder={t('Enter URL')} disabled={disabled} />;
      default:
        return <Input placeholder={label} disabled={disabled} />;
    }
  };

  /**
   * Render appropriate form input based on field type
   * Returns the correct Ant Design component for each field
   */
  const getFormItem = (fieldToShow: any, label: string) => {
    const type = fieldToShow.type || undefined;
    const disabled = isFieldDisabled(type, action);

    if (fieldToShow.isCustom) {
      return renderCustomField(fieldToShow, label, disabled);
    }

    switch (fieldToShow.key) {
      case 'vehicle_number':
      case 'vehicle_code':
      case 'renavam_number':
      case 'code':
      case 'sensor_code':
        return renderInputBox(label, disabled);
      case 'time_slot_start':
      case 'time_slot_end':
        return <TimePicker format="HH:mm" disabled={disabled} style={{ width: '100%' }} />;
      case 'is_available_for_midmile':
      case 'is_available_for_endpoint':
        return <Checkbox disabled={disabled} />;
      case 'hub_code':
        return renderHubCodeSelect(hubOptions, handleHubSearch, disabled, t);
      case 'vehicle_make_code': {
        return renderSelect(vehicleMakes, label, disabled);
      }
      case 'primary_tracking_source':
        return renderSelect(primaryTrackingSources, label, disabled);
      case 'vehicle_vendor_code':
        return renderVendorSelect(disabled);
      case 'state': {
        return renderSelect(stateOptions, label, disabled, false, stateLoading);
      }
      case 'working_days': {
        const workingDayOptions = (weekDays || []).map((day) => ({
          label: day.label,
          value: day.value,
        }));
        return renderSelect(workingDayOptions, label, disabled, true);
      }
      case 'fleet_type':
      case 'number_of_axles':
        return renderFixedListSelect(fieldToShow.key, label, disabled);
      case 'vehicle_year':
        return <Input type="number" placeholder={label} disabled={disabled} />;
      default:
        return null;
    }
  };

  return (
    <Modal
      visible={isVisible}
      onCancel={handleModalClose}
      maskClosable={false}
      footer={null}
      title={t(action === 'edit' ? 'Update Vehicle' : 'Add Vehicle')}
      width={1050}
    >
      <Form
        form={form}
        onFinish={handleSubmit}
        layout="horizontal"
        labelCol={{ span: 8 }}
        wrapperCol={{ span: 16 }}
        labelAlign="left"
        initialValues={{
          working_days: [],
        }}
      >
        <Row gutter={18}>
          {/* Dynamically render form fields based on configuration */}
          {Object.entries(fieldConfigMap).map(([key, item]: any) => {
            const label = item.label || defaultLabels[item.key] || item.key;
            const isCheckbox = ['is_available_for_midmile', 'is_available_for_endpoint'].includes(key);

            return (
              <Col span={12} key={key}>
                <Form.Item
                  label={label}
                  name={key}
                  colon={false}
                  rules={getValidationRules(key, label, item.is_optional)}
                  style={{ marginBottom: '24px' }}
                  valuePropName={isCheckbox ? 'checked' : 'value'}
                >
                  {getFormItem(item, label)}
                </Form.Item>
              </Col>
            );
          })}
        </Row>
        <Form.Item style={{ marginTop: '16px', marginBottom: 0 }}>
          <Button onClick={handleModalClose} style={{ marginRight: '10px' }}>
            {t('Cancel')}
          </Button>
          <Button type="primary" htmlType="submit" loading={confirmLoading}>
            {t('Submit')}
          </Button>
        </Form.Item>
      </Form>
    </Modal>
  );
};

const mapStateToProps = ({ masterData }: any) => {
  const {
    vendor_config,
    vehicle_validation_strategy = {},
    primary_tracking_sources = [],
    vehicle_custom_fields = [],
  } = masterData;
  const { vehicle_master_config } = vendor_config || {};
  const customFields = (vehicle_custom_fields || []).map(
    (field: any) => ({ ...field, isCustom: true }),
  );
  return {
    fields_list: [...(vehicle_master_config?.fields_list || []), ...customFields],
    vehicle_validation_strategy: vehicle_validation_strategy || {},
    primaryTrackingSources: primary_tracking_sources || [],
  };
};

const hocConfig: HocOptions = {
  connectRedux: {
    useRedux: true,
    mapStateToProps,
  },
  connectRouter: true,
  connectTranslession: true,
};

export default GenericHoc(hocConfig)(AddNewVehicleModal);
